import { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import api from "../../utils/axiosConfig";
import { fadeInUp, fadeInDown, scaleIn, stagger } from "../../utils/animations";

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/notifications/");
      const data = Array.isArray(res.data) ? res.data : [];
      data.sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation));
      setNotifications(data);
    } catch {
      setError("Impossible de charger les notifications.");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.patch(`/api/notifications/${id}/lu`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, lu: true } : n)));
    } catch {
      // silencieux
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch("/api/notifications/marquer-tout-lu");
      setNotifications((prev) => prev.map((n) => ({ ...n, lu: true })));
    } catch {
      // silencieux
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const unreadCount = notifications.filter((n) => !n.lu).length;

  const displayed = useMemo(
    () => (showAll ? notifications : notifications.filter((n) => !n.lu)),
    [notifications, showAll]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
              {/* ── Header violet ── */}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="purple"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
              >
                <MDBox>
                  <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                    Notifications
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                    {unreadCount > 0
                      ? `${unreadCount} non lue${unreadCount > 1 ? "s" : ""}`
                      : "Toutes les notifications sont lues"}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" gap={1} flexWrap="wrap">
                  {unreadCount > 0 && (
                    <MDButton variant="outlined" color="white" size="small" onClick={markAllAsRead}>
                      <Icon sx={{ mr: 0.5 }}>done_all</Icon>
                      Tout marquer comme lu
                    </MDButton>
                  )}
                  <MDButton
                    variant={showAll ? "contained" : "outlined"}
                    color="white"
                    size="small"
                    onClick={() => setShowAll((v) => !v)}
                  >
                    <Icon sx={{ mr: 0.5 }}>{showAll ? "visibility_off" : "visibility"}</Icon>
                    {showAll ? "Masquer les lues" : "Voir toutes"}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="white"
                    size="small"
                    onClick={fetchNotifications}
                  >
                    <Icon sx={{ mr: 0.5 }}>refresh</Icon>
                    Actualiser
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* ── Contenu ── */}
              <MDBox pt={2} pb={2} px={2}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : error ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="h6" color="error">
                      {error}
                    </MDTypography>
                  </MDBox>
                ) : displayed.length === 0 ? (
                  <MDBox
                    p={4}
                    textAlign="center"
                    sx={{ animation: `${scaleIn} 0.5s ease-out both` }}
                  >
                    <Icon sx={{ fontSize: 48, color: "grey.400", display: "block", mb: 1 }}>
                      {showAll ? "notifications_none" : "done_all"}
                    </Icon>
                    <MDTypography variant="h6" color="text">
                      {showAll
                        ? "Aucune notification pour le moment"
                        : "Aucune notification non lue"}
                    </MDTypography>
                    {!showAll && notifications.length > 0 && (
                      <MDButton
                        variant="text"
                        color="info"
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setShowAll(true)}
                      >
                        Voir l&apos;historique complet
                      </MDButton>
                    )}
                  </MDBox>
                ) : (
                  displayed.map((notif, idx) => (
                    <MDBox key={notif.id}>
                      <MDBox
                        display="flex"
                        alignItems="flex-start"
                        p={1.5}
                        borderRadius="lg"
                        onClick={() => !notif.lu && markAsRead(notif.id)}
                        sx={{
                          animation: `${fadeInUp} 0.4s ease-out ${stagger(idx, 0.06)} both`,
                          borderLeft: notif.lu ? "3px solid transparent" : "3px solid #1A73E8",
                          bgcolor: notif.lu ? "transparent" : "rgba(26,115,232,0.04)",
                          cursor: notif.lu ? "default" : "pointer",
                          opacity: notif.lu ? 0.65 : 1,
                          transition: "background 0.2s, opacity 0.2s",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.03)", opacity: 1 },
                        }}
                      >
                        {/* Icône */}
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width="38px"
                          height="38px"
                          borderRadius="50%"
                          sx={{
                            bgcolor: notif.lu ? "grey.200" : "info.main",
                            flexShrink: 0,
                            mr: 2,
                          }}
                        >
                          <Icon sx={{ color: notif.lu ? "grey.600" : "white", fontSize: 20 }}>
                            {notif.texte_id ? "description" : "notifications"}
                          </Icon>
                        </MDBox>

                        {/* Texte */}
                        <MDBox flex={1}>
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            <MDTypography
                              variant="button"
                              fontWeight={notif.lu ? "regular" : "bold"}
                              color="dark"
                            >
                              {notif.message}
                            </MDTypography>
                            <Chip
                              label={notif.lu ? "Lu" : "Non lu"}
                              size="small"
                              color={notif.lu ? "default" : "info"}
                              sx={{ fontSize: "0.65rem", height: 22 }}
                            />
                          </MDBox>
                          <MDTypography variant="caption" color="text" mt={0.5}>
                            {formatDate(notif.date_creation)}
                          </MDTypography>
                          {!notif.lu && (
                            <MDTypography
                              variant="caption"
                              color="info"
                              sx={{ display: "block", mt: 0.3, opacity: 0.7 }}
                            >
                              Cliquer pour marquer comme lu
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                      {idx < displayed.length - 1 && <Divider sx={{ my: 0.5, opacity: 0.5 }} />}
                    </MDBox>
                  ))
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Notifications;
